import { DEFAULT_PLATFORM_PERCENT } from '../types/billing.js';
/**
 * Write-path ceilings on what EVI's pricing can be asked to fund.
 *
 * Electricity rates, split by shape (Warren, 2026-09-27, superseding the
 * one-number ceiling of 2026-09-26):
 *
 *   "Flat and blended rates cap at 40 cents, since a flat rate above that
 *   is a typo or a lie. TOU and per-hour rows cap at 70 cents, since a
 *   California peak is real and EV2-A sits at 62. The refusal at
 *   settlement is the guard against harm; the ceiling is a guard against
 *   typos, and it should not refuse PG&E's published tariff. The
 *   homeowner's self-entered rate goes on the same constant, 40 flat, 70
 *   on a TOU window."
 *
 * Raised to 80 the same day (Warren, 2026-09-27): "It is a typo guard, not
 * the harm guard; the refusal at settlement is the harm guard. Seventy
 * versus eighty changes nothing about what a bad rate can do and stops the
 * cap from refusing a published California tariff. Flat stays at 40."
 * (SCE TOU-D 5-8 PM summer peaks at 74.)
 *
 *   FLAT (40, now 60 with a second confirmation above 40: see below): a
 *   flat per-kWh rate, a TOU tariff's base/fallback rate, a tier row, and
 *   the homeowner's flat or base rate.
 *   TOU (80): a time-of-use row (inline window or tariff schedule entry)
 *   and ANY per-hour rate (a flat rate billed per hour, a per-hour window,
 *   a per_hour tariff's rates): per-hour is not a per-kWh price, so the
 *   "a flat rate above that is a typo" argument does not reach it.
 *
 * Applied on every writer of a Site's electricity reimbursement (the
 * Location's flat rate, its inline TOU windows, a Tariff bound as its
 * electricity_tariff_id) and of the homeowner's informational utility
 * rate. A rate that gets past them is caught at settlement: reimbursement
 * above the session's energy revenue is held for a manual payout
 * (@evi/billing payout-calculator), never clamped.
 *
 * The flat cap raised to 60 (Warren, 21 October 2026, night): "raise the
 * utility and reimbursement caps to 60 cents, with a second confirmation
 * above 40". His utility rate file has Hawaii at $0.4363 per kWh (EIA,
 * July 2026 commercial average), which the 40 cent cap refused.
 *
 *   FLAT (60): the hard cap on a flat per-kWh rate, the Site's
 *   reimbursement and EVI's utility rate table (driverPrice.ts
 *   UTILITY_COST_MAX_CENTS). Above it is refused, as above 40 was.
 *   CONFIRM ABOVE (40): a person saving a flat per-kWh rate above 40 and
 *   at most 60 confirms it a second time (the request's
 *   confirm_above_40). A writer with no second confirmation keeps 40 as
 *   its limit, exactly as before: the CMS provisioning create and a
 *   Tariff bound as a Site's electricity.
 *   TOU (80): unchanged by the ruling.
 *
 * Two writers left at 40 that night moved to 60 (Warren, 21 October 2026,
 * night: "Change: the Mcp tool's cap and a homeowner's own rate cap go to
 * 60 cents, like the rest."): the MCP provision_tenant tool (POST
 * /provisioning/tenant) and the homeowner's own rate (PATCH /me/home/rate
 * and the driver app), each with the same confirm_above_40 above 40.
 * Everything that reads a stored rate (the driver price floor, the
 * electricity payback, settlement) takes it up to 60.
 *
 * Percentage coupons: "Cap at 15% in the editor, refuse creation above
 * it. EVI funds coupons out of its 15% and cannot give away more than
 * it has." Tied to the platform percentage so the two cannot drift.
 * Fixed-amount coupons have no ceiling here; settlement refuses one
 * that exceeds EVI's headroom and holds it for a later session.
 */
export const ELECTRICITY_RATE_FLAT_MAX_CENTS = 60;
export const ELECTRICITY_RATE_FLAT_CONFIRM_ABOVE_CENTS = 40;
export const ELECTRICITY_RATE_TOU_MAX_CENTS = 80;
/**
 * The cap a write may store. A flat per-kWh rate caps at 60 on a writer
 * that asks a person for the second confirmation above 40 (`confirmable`),
 * and at 40 on every other writer (Warren, 21 October 2026, night).
 */
export function electricityRateMaxCents(shape, rateType, confirmable = false) {
    if (shape === 'tou' || rateType === 'per_hour')
        return ELECTRICITY_RATE_TOU_MAX_CENTS;
    return confirmable ? ELECTRICITY_RATE_FLAT_MAX_CENTS : ELECTRICITY_RATE_FLAT_CONFIRM_ABOVE_CENTS;
}
/** A flat per-kWh rate above 40 and at most 60: saved only with the second confirmation. */
export function rateNeedsConfirmation(rateCents) {
    return typeof rateCents === 'number'
        && Number.isFinite(rateCents)
        && rateCents > ELECTRICITY_RATE_FLAT_CONFIRM_ABOVE_CENTS
        && rateCents <= ELECTRICITY_RATE_FLAT_MAX_CENTS;
}
/** Cents per kWh as a person writes them: 43.63, 45, 14.5 (at most four decimals). */
export function rateCentsText(cents) {
    return String(Number(cents.toFixed(4)));
}
/** Warren's second step, with the rate: "43.63 cents per kWh is above 40 cents. Confirm this rate." */
export function rateConfirmMessage(cents) {
    return `${rateCentsText(cents)} cents per kWh is above ${ELECTRICITY_RATE_FLAT_CONFIRM_ABOVE_CENTS} cents. Confirm this rate.`;
}
/**
 * A rate in cents to a hundredth of a cent, the precision a Site's flat rate and the utility table store (Warren, 21
 * October 2026, night: "The location form stores rates to a hundredth of a cent, so 43.63 stays 43.63."). The float noise
 * of a scaled input goes first, so 43.635 rounds half up to 43.64 and 0.4363 dollars is 43.63, never 43.629999.
 */
export function rateToHundredthCent(cents) {
    return Math.round(Number((cents * 100).toFixed(6))) / 100;
}
/** A rate with at most two decimals in cents (43.63, not 43.635), as rateToHundredthCent stores it. */
export function isRateToHundredthCent(cents) {
    return Number.isFinite(cents) && Math.abs(cents * 100 - Math.round(cents * 100)) <= 1e-6;
}
/**
 * The homeowner's own rates a save confirms a second time (Warren, 21 October 2026, night: "Change: the Mcp tool's cap
 * and a homeowner's own rate cap go to 60 cents, like the rest."): each flat per-kWh rate in it above 40 and at most 60,
 * the flat or time-of-use base rate (not on a tiered save, which re-sends the stored base its editor hides) and every
 * tier, that is not the rate already stored in its place, so a save re-sending a confirmed rate is not asked again, as a
 * location's is not. A time-of-use window caps at 80 and is never asked. PATCH /me/home/rate and the driver app read the
 * same list.
 */
export function homeRatesNeedingConfirmation(save, stored) {
    const asked = [];
    const base = save.rate_cents_per_kwh;
    if (save.rate_type !== 'tiered' && rateNeedsConfirmation(base) && base !== stored?.rate_cents_per_kwh)
        asked.push(base);
    (save.tiers ?? []).forEach((tier, i) => {
        const rate = tier?.rate_cents_per_kwh;
        if (rateNeedsConfirmation(rate) && rate !== stored?.tiers?.[i]?.rate_cents_per_kwh)
            asked.push(rate);
    });
    return asked;
}
/** A rate EVI will store under `maxCents`. null/undefined = not set (allowed). */
export function isWithinElectricityCeiling(rateCents, maxCents) {
    if (rateCents === null || rateCents === undefined)
        return true;
    return typeof rateCents === 'number'
        && Number.isFinite(rateCents)
        && rateCents >= 0
        && rateCents <= maxCents;
}
function ceilingSubject(maxCents) {
    return maxCents === ELECTRICITY_RATE_TOU_MAX_CENTS
        ? 'A time-of-use or per-hour electricity rate'
        : 'A flat electricity rate';
}
function ceilingAmount(maxCents, display) {
    return display === 'dollars'
        ? `$${(maxCents / 100).toFixed(2)} (enter ${(maxCents / 100).toFixed(2)} or less)`
        : `${maxCents}¢`;
}
/**
 * A Site's reimbursement rate refused: says the cap, in the field's unit.
 * At 40 (a writer with no second confirmation) it says where a rate up to
 * 60 is set instead (Warren, 21 October 2026, night).
 */
export function siteElectricityCeilingMessage(maxCents, display = 'cents') {
    if (maxCents === ELECTRICITY_RATE_FLAT_CONFIRM_ABOVE_CENTS) {
        const upTo = display === 'dollars' ? `$${(ELECTRICITY_RATE_FLAT_MAX_CENTS / 100).toFixed(2)}` : `${ELECTRICITY_RATE_FLAT_MAX_CENTS}¢`;
        return `${ceilingSubject(maxCents)} can be at most ${ceilingAmount(maxCents, display)} here. `
            + `A rate up to ${upTo} is set on the location in Admin, with a second confirmation.`;
    }
    return `${ceilingSubject(maxCents)} can be at most ${ceilingAmount(maxCents, display)}. `
        + 'EVI does not reimburse electricity above that.';
}
/** The homeowner's own (informational) utility rate refused: a typo guard, not a reimbursement rule. */
export function homeElectricityCeilingMessage(maxCents, display = 'dollars') {
    return `${ceilingSubject(maxCents)} can be at most ${ceilingAmount(maxCents, display)} per kWh. `
        + 'Check the rate on your utility bill.';
}
/**
 * Billable idle minutes per session, network-wide (Warren, 2026-09-27):
 * "IDLE CAP: 60 minutes per session. An idle fee is a deterrent against a
 * car sitting on a stall after it is done, not a revenue line, and $720 for
 * a twelve-hour dwell is a chargeback with a number attached. At the 100
 * cent home maximum that is $60; at a typical 25 cents it is $15. The hold
 * stays sized for energy; idle goes to overage on the card already on
 * file." Applied by @evi/billing computeIdleMinutes after the grace, and
 * disclosed wherever the idle fee is shown ("up to 60 minutes").
 */
export const IDLE_FEE_MAX_MINUTES = 120;
/**
 * The idle fee's grace period, whole minutes, network-wide (Warren,
 * 2026-09-28): "A grace period is REQUIRED whenever an idle fee is set,
 * host-programmable from 5 to 30 minutes, default 15. The earn walk and
 * the charger page refuse an idle fee without a grace, and the driver's
 * pre-session screen shows both: 'after charging, 15 minutes free, then
 * 25 cents a minute, up to 60 minutes.'"
 *
 * Stored on the tariff beside the fee (Tariff.rates.idle_grace_minutes),
 * pinned onto the Session at start with the rate, and taken off the idle
 * minutes before the IDLE_FEE_MAX_MINUTES cap (@evi/billing
 * computeIdleMinutes), so the cap counts billed minutes: 15 free, then up
 * to 60 billed. Before this ruling nothing passed a grace to billing, so
 * idle billed from the first whole minute after charging stopped.
 */
export const IDLE_GRACE_MIN_MINUTES = 5;
export const IDLE_GRACE_MAX_MINUTES = 30;
export const IDLE_GRACE_DEFAULT_MINUTES = 15;
/**
 * The idle fee's per-minute ceiling, cents, network-wide (Warren,
 * 2026-09-29): "$2 a minute maximum."
 * residentialFees.ts HOME_IDLE_RATE_MAX_CENTS is this number.
 */
export const IDLE_RATE_MAX_CENTS_PER_MIN = 200;
/** A grace EVI will store: a whole number of minutes from 5 to 30. */
export function isValidIdleGraceMinutes(v) {
    return typeof v === 'number'
        && Number.isInteger(v)
        && v >= IDLE_GRACE_MIN_MINUTES
        && v <= IDLE_GRACE_MAX_MINUTES;
}
export const IDLE_GRACE_MESSAGE = `An idle fee needs a grace period of ${IDLE_GRACE_MIN_MINUTES} to ${IDLE_GRACE_MAX_MINUTES} minutes, `
    + 'counted from when charging stops.';
export const IDLE_RATE_MAX_MESSAGE = `An idle fee can be at most $${(IDLE_RATE_MAX_CENTS_PER_MIN / 100).toFixed(2)} a minute.`;
/**
 * The grace a write stores alongside an idle fee: the one sent when valid,
 * else the one already stored when valid, else the default. Only for a
 * caller that has already refused an INVALID sent value; `sent` here is
 * undefined (not sent) or valid. A stale client that sends an idle fee
 * without a grace keeps the host's stored grace, or gets the default,
 * rather than a 400 that strands it mid-flow.
 */
export function resolveIdleGraceMinutes(sent, stored) {
    if (isValidIdleGraceMinutes(sent))
        return sent;
    if (isValidIdleGraceMinutes(stored))
        return stored;
    return IDLE_GRACE_DEFAULT_MINUTES;
}
export const EVI_COUPON_MAX_PERCENT = DEFAULT_PLATFORM_PERCENT;
export const EVI_COUPON_PERCENT_MESSAGE = `A percentage coupon can be at most ${EVI_COUPON_MAX_PERCENT}%. `
    + `EVI funds coupons out of its ${EVI_COUPON_MAX_PERCENT}% and cannot give away more than it has.`;
//# sourceMappingURL=pricingCeilings.js.map